import type { AppointmentWithDetails } from '@garage/shared'

/**
 * Build a /jobcards/new URL with appointment data as query params,
 * exactly as CalendarPage's handleCreateJobcard did before.
 *
 * - appointmentId is always included
 * - clientId, carId, and reason (as complaint) are included when set
 * - apptName, apptPhone, apptCar are included ONLY when clientId is null
 */
export function appointmentJobcardUrl(appointment: AppointmentWithDetails): string {
  const params = new URLSearchParams()
  params.set('appointmentId', appointment.id)
  if (appointment.clientId) {
    params.set('clientId', appointment.clientId)
  }
  if (appointment.carId) {
    params.set('carId', appointment.carId)
  }
  if (appointment.reason) {
    params.set('complaint', appointment.reason)
  }
  // Only when NO clientId: add apptName, apptPhone, apptCar
  if (!appointment.clientId) {
    if (appointment.customerLabel) {
      params.set('apptName', appointment.customerLabel)
    }
    if (appointment.phoneLabel) {
      params.set('apptPhone', appointment.phoneLabel)
    }
    if (appointment.carLabel) {
      params.set('apptCar', appointment.carLabel)
    }
  }
  return `/jobcards/new?${params.toString()}`
}

/**
 * Format a time string (HH:mm) into 12-hour format with AM/PM.
 * Returns null if the input is null or empty.
 *
 * Examples:
 *   '09:00' -> '9:00 AM'
 *   '14:30' -> '2:30 PM'
 *   '12:00' -> '12:00 PM'
 *   null -> null
 */
export function formatAppointmentTime(time: string | null): string | null {
  if (!time) return null
  const [h, min] = time.split(':').map(Number)
  const period = h! >= 12 ? 'PM' : 'AM'
  const hour12 = h! % 12 === 0 ? 12 : h! % 12
  return `${hour12}:${String(min).padStart(2, '0')} ${period}`
}
